import type { MarketHours } from '../api/types'

/**
 * Parse FMP time string like "09:30 AM -04:00" into hours and minutes in UTC.
 */
function parseTimeToUTCMinutes(timeStr: string): number {
  // Format: "HH:MM AM/PM -HH:MM" or "HH:MM AM/PM +HH:MM"
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)\s*([+-]\d{2}):(\d{2})$/i)
  if (!match) return -1

  let hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  const ampm = match[3].toUpperCase()
  const offsetSign = match[4].startsWith('-') ? -1 : 1
  const offsetHours = parseInt(match[4].replace(/[+-]/, ''), 10)
  const offsetMinutes = parseInt(match[5], 10)

  // Convert 12h to 24h
  if (ampm === 'PM' && hours !== 12) hours += 12
  if (ampm === 'AM' && hours === 12) hours = 0

  // Convert to UTC minutes
  const totalMinutes = hours * 60 + minutes
  const offsetTotalMinutes = offsetSign * (offsetHours * 60 + offsetMinutes)

  return totalMinutes - offsetTotalMinutes
}

/**
 * Check if a market is currently open based on its cached schedule.
 * Returns true if current UTC time is within opening-closing hours and it's a weekday.
 */
export function isMarketOpenNow(mh: MarketHours): boolean {
  const now = new Date()
  const dayOfWeek = now.getUTCDay() // 0=Sun, 6=Sat

  // Weekends are always closed
  if (dayOfWeek === 0 || dayOfWeek === 6) return false

  const openUTC = parseTimeToUTCMinutes(mh.openingHour)
  const closeUTC = parseTimeToUTCMinutes(mh.closingHour)

  if (openUTC < 0 || closeUTC < 0) {
    // Couldn't parse → fall back to API value
    return mh.isMarketOpen
  }

  const nowUTC = now.getUTCHours() * 60 + now.getUTCMinutes()

  // Handle normal case (open < close, e.g. 13:30 - 20:00 UTC)
  if (openUTC <= closeUTC) {
    return nowUTC >= openUTC && nowUTC < closeUTC
  }

  // Handle overnight case (open > close, e.g. 22:00 - 06:00 UTC)
  return nowUTC >= openUTC || nowUTC < closeUTC
}
